/**
 * ShippingNoticePage 出貨通知 (S6.1) — Board D #6, 船務 Shipping nav.
 *
 * Split out of the codex `ShippingPages.tsx` onto the house list stack
 * (useListPage + GenericFilterPopover + TableContainer state triad).
 *
 * The 白單/黃單 flow:
 * - 待安排 draft — notice drafted for one vendor on one open 裝櫃批次
 * - 發出白單 issue → 已通知供應商 sent (estimates hit the OCC buckets)
 * - 登錄黃單回簽 counter-sign capture → 已回簽 acknowledged — the factory's
 *   confirmed quantities feed A2 出貨通知再確認 server-side
 *
 * The notice enum ends at `acknowledged` (types/mocks frozen to other
 * slices); the plan's 已出貨 terminal state needs a `shipped` status —
 * recorded as an S6.2 follow-up, not faked with a mislabel here.
 */

import { useMemo, useState } from 'react';
import { App, Badge, Button, Dropdown, Input, Table, Tag, theme } from 'antd';
import type { TableProps } from 'antd';
import {
  AuditOutlined,
  FilterOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import GenericFilterPopover from '@/components/common/GenericFilterPopover';
import type { FieldConfig, FilterValues } from '@/components/common/GenericFilterPopover';
import { HighlightText } from '@/components/common/HighlightText';
import { useListPage } from '@/hooks/useListPage';
import { extractApiError } from '@/hooks/useSalesOrders';
import {
  useShipmentBatchOptions,
  useShippingNoticeMutations,
  useShippingNoticesTable,
} from '@/hooks/useShipping';
import { useShippingStore } from '@/stores/shippingStore';
import type { NoticeFilters, NoticeStatusFilter } from '@/stores/shippingStore';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import type { ShippingNotice, ShippingNoticeStatus } from '@/types/api/shipping';
import NoticeCreateModal from './components/NoticeCreateModal';
import NoticeCounterSignModal from './components/NoticeCounterSignModal';

/** antd Tag presets per status (no colour literals — house rule). */
const STATUS_TAG_COLOR: Record<ShippingNoticeStatus, string> = {
  draft: 'default',
  sent: 'blue',
  acknowledged: 'green',
};

const NOTICE_STATUS_ORDER: ShippingNoticeStatus[] = ['draft', 'sent', 'acknowledged'];

interface NoticeFilterPopoverValues extends FilterValues {
  status?: string;
}

const ShippingNoticePage = () => {
  const { t } = useTranslation(['shipping', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();

  const { noticeFilters, setNoticeSearch, setNoticeStatus, resetNoticeFilters } =
    useShippingStore();
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // Record + open are decoupled so the closing animation keeps its content.
  const [counterSignNoticeRow, setCounterSignNoticeRow] = useState<ShippingNotice | null>(null);
  const [counterSignOpen, setCounterSignOpen] = useState(false);

  const customFilterCounters = useMemo(
    () => ({ status: (value: unknown) => (value === 'all' ? 0 : 1) }),
    []
  );

  const { tableProps, isLoading, refresh, activeFilterCount, isFiltered, listState } = useListPage<
    NoticeFilters,
    ShippingNotice
  >({
    useTableHook: useShippingNoticesTable,
    filters: noticeFilters,
    filterCountExclusions: ['search'],
    customFilterCounters,
  });

  const { batches } = useShipmentBatchOptions();
  const { issueNotice } = useShippingNoticeMutations();

  const batchNumberById = useMemo(
    () => new Map(batches.map((batch) => [batch.id, batch.batchNumber])),
    [batches]
  );

  const handleIssueWhite = async (notice: ShippingNotice) => {
    try {
      await issueNotice(notice.id);
      message.success(t('shipping:notice.messages.whiteIssued'));
      refresh();
    } catch (error) {
      const body = extractApiError(error);
      message.error(body.message || t('common:messages.updateFailed'));
    }
  };

  const filterFields: FieldConfig[] = useMemo(
    () => [
      {
        key: 'status',
        label: t('shipping:notice.fields.status'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('shipping:notice.filters.allStatuses') },
          ...NOTICE_STATUS_ORDER.map((status) => ({
            value: status,
            label: t(`shipping:notice.status.${status}`),
          })),
        ],
        resetText: t('common:actions.reset'),
      },
    ],
    [t]
  );

  const columns: TableProps<ShippingNotice>['columns'] = [
    {
      title: t('shipping:notice.fields.noticeNumber'),
      dataIndex: 'noticeNumber',
      key: 'noticeNumber',
      width: 190,
      render: (_: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>
            <HighlightText
              text={record.noticeNumber}
              searchTerm={noticeFilters.search}
              fallback="-"
            />
          </span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {dayjs(record.createdAt).format('YYYY-MM-DD')}
          </span>
        </div>
      ),
    },
    {
      title: t('shipping:notice.fields.vendor'),
      dataIndex: 'vendorName',
      key: 'vendorName',
      width: 240,
      render: (value: string) => (
        <HighlightText text={value} searchTerm={noticeFilters.search} fallback="-" />
      ),
    },
    {
      title: t('shipping:notice.fields.batch'),
      dataIndex: 'batchId',
      key: 'batchId',
      width: 150,
      render: (batchId: string) => {
        const batchNumber = batchNumberById.get(batchId);
        return batchNumber ? <Tag style={{ margin: 0 }}>{batchNumber}</Tag> : renderDash();
      },
    },
    {
      title: t('shipping:notice.fields.qty'),
      key: 'qty',
      width: 150,
      align: 'right',
      render: (_: unknown, record) => {
        const estimated = record.lines.reduce((sum, line) => sum + line.qtyEstimated, 0);
        const confirmed = record.lines.reduce((sum, line) => sum + line.qtyConfirmed, 0);
        return (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {estimated} / {record.status === 'acknowledged' ? confirmed : '-'}
          </span>
        );
      },
    },
    {
      title: t('shipping:notice.fields.slips'),
      key: 'slips',
      width: 190,
      render: (_: unknown, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          {record.sentAt ? (
            <span style={{ fontSize: token.fontSizeSM }}>
              {t('shipping:notice.slips.white', { date: dayjs(record.sentAt).format('YYYY-MM-DD') })}
            </span>
          ) : (
            renderDash()
          )}
          {record.acknowledgedAt && (
            <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
              {t('shipping:notice.slips.yellow', {
                date: dayjs(record.acknowledgedAt).format('YYYY-MM-DD'),
              })}
            </span>
          )}
        </div>
      ),
    },
    {
      title: t('shipping:notice.fields.status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: ShippingNoticeStatus) => (
        <Tag color={STATUS_TAG_COLOR[status]} style={{ margin: 0 }}>
          {t(`shipping:notice.status.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('shipping:notice.fields.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record) => {
        // hide-don't-disable: only render the actions a status affords
        const items = [
          ...(record.status === 'draft'
            ? [
                {
                  key: 'issueWhite',
                  label: t('shipping:notice.actions.issueWhite'),
                  icon: <SendOutlined />,
                  onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                    e.domEvent.stopPropagation();
                    void handleIssueWhite(record);
                  },
                },
              ]
            : []),
          ...(record.status === 'sent'
            ? [
                {
                  key: 'counterSign',
                  label: t('shipping:notice.actions.counterSign'),
                  icon: <AuditOutlined />,
                  onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                    e.domEvent.stopPropagation();
                    setCounterSignNoticeRow(record);
                    setCounterSignOpen(true);
                  },
                },
              ]
            : []),
        ];
        if (items.length === 0) return renderDash();
        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button
              type="text"
              aria-label={t('shipping:notice.actions.actionsFor', {
                noticeNumber: record.noticeNumber,
              })}
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="tw-px-6 tw-pb-8">
      <div className="tw-mb-6">
        <h1 style={{ fontSize: token.fontSizeHeading2, fontWeight: token.fontWeightStrong }}>
          {t('shipping:notice.list.title')}
        </h1>
      </div>

      {/* Search + status filter + create toolbar */}
      <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
        <Input
          placeholder={t('shipping:notice.searchPlaceholder')}
          value={noticeFilters.search}
          onChange={(e) => setNoticeSearch(e.target.value)}
          variant="outlined"
          size="large"
          suffix={<SearchOutlined style={{ fontSize: token.sizeMD, color: token.colorPrimary }} />}
          style={{
            width: '450px',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        />
        <GenericFilterPopover<NoticeFilterPopoverValues>
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          onApply={(values) => setNoticeStatus((values.status as NoticeStatusFilter) || 'all')}
          initialFilters={{ status: noticeFilters.status }}
          fields={filterFields}
          applyText={t('common:actions.apply')}
          cancelText={t('common:actions.cancel')}
        >
          <Badge count={activeFilterCount}>
            <Button
              aria-label={t('common:actions.filter')}
              icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
              size="large"
              style={{
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
                width: `${controlHeightTokens.controlHeightXL}px`,
              }}
            />
          </Badge>
        </GenericFilterPopover>
        <div className="tw-flex tw-gap-3" style={{ marginLeft: 'auto' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => setCreateOpen(true)}
            style={{
              borderRadius: radiusTokens.card,
              height: `${controlHeightTokens.controlHeightXL}px`,
              paddingLeft: token.paddingLG,
              paddingRight: token.paddingLG,
            }}
          >
            {t('shipping:notice.addNotice')}
          </Button>
        </div>
      </div>

      {/* Table — S0.3 state triad via TableContainer + ListEmptyState */}
      <TableContainer
        errorState={{
          active: listState === 'error',
          title: t('shipping:notice.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<ShippingNotice>
          {...tableProps}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          locale={{
            emptyText: isFiltered ? (
              <ListEmptyState
                title={t('shipping:notice.states.noResults')}
                actionLabel={t('shipping:notice.states.clearFilters')}
                onAction={resetNoticeFilters}
                actionType="default"
              />
            ) : (
              <ListEmptyState
                title={t('shipping:notice.states.noNotices')}
                actionLabel={t('shipping:notice.addNotice')}
                onAction={() => setCreateOpen(true)}
                actionIcon={<PlusOutlined />}
              />
            ),
          }}
          pagination={{ ...tableProps.pagination, showSizeChanger: false }}
          scroll={{ x: 980 }}
        />
      </TableContainer>

      <NoticeCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={refresh}
      />
      <NoticeCounterSignModal
        open={counterSignOpen}
        notice={counterSignNoticeRow}
        onClose={() => setCounterSignOpen(false)}
        onDone={refresh}
      />
    </div>
  );
};

export default ShippingNoticePage;
